import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { configureClient } from '../api/client';
import { login as apiLogin, refreshToken as apiRefreshToken } from '../api/endpoints';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Access token lives only in memory / BroadcastChannel messages — never in
// localStorage — to avoid widening XSS blast radius. Refresh token is an
// httpOnly cookie set by the server.
const REFRESH_BUFFER_MS = 60_000; // refresh 60 s before expiry

// --- Cross-tab coordination -------------------------------------------
//
// The refresh token rotates server-side on every /auth/refresh call. Two
// browser tabs open at once (e.g. /frota and /map) each run their own
// AuthProvider, so an in-memory, per-tab dedupe (refreshInFlightRef below)
// isn't enough — tab A can refresh, rotating the cookie, right as tab B's
// own timer fires and sends the now-stale cookie, killing a session that
// was otherwise fine.
//
// BroadcastChannel lets a tab that just refreshed hand the new token to
// its siblings instead of them each hitting the network. localStorage is
// used only for a short-lived, valueless lock (a timestamp) so concurrent
// tabs don't both start a network refresh at once — the token itself never
// touches localStorage.
interface BroadcastAuthMessage {
  type: 'refreshed';
  accessToken: string;
  user: User;
}

const AUTH_CHANNEL_NAME = 'rastreamento-auth-v1';
const REFRESH_LOCK_KEY = 'rastreamento-refresh-lock-v1';
const REFRESH_LOCK_TTL_MS = 4_000;
const BROADCAST_WAIT_MS = 1_500;

const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

function tryAcquireRefreshLock(): boolean {
  try {
    const held = localStorage.getItem(REFRESH_LOCK_KEY);
    if (held && Date.now() - Number(held) < REFRESH_LOCK_TTL_MS) {
      return false;
    }
    localStorage.setItem(REFRESH_LOCK_KEY, String(Date.now()));
    return true;
  } catch {
    return true; // localStorage unavailable (private mode, etc.) — no cross-tab coordination possible
  }
}

function releaseRefreshLock(): void {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    // ignore
  }
}

// Waits for a sibling tab's 'refreshed' broadcast instead of racing it.
function waitForSiblingRefresh(timeoutMs: number): Promise<BroadcastAuthMessage | null> {
  if (!authChannel) return Promise.resolve(null);

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      authChannel.removeEventListener('message', onMessage);
      resolve(null);
    }, timeoutMs);

    function onMessage(event: MessageEvent<BroadcastAuthMessage>) {
      if (event.data.type !== 'refreshed') return;
      clearTimeout(timer);
      authChannel!.removeEventListener('message', onMessage);
      resolve(event.data);
    }

    authChannel.addEventListener('message', onMessage);
  });
}

function parseTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (typeof payload.exp === 'number') {
      return payload.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The refresh token rotates server-side on every call, so two /auth/refresh
  // requests in flight at once race: the second lands with an already-rotated
  // cookie and comes back 401, killing a session that was otherwise fine.
  // Every trigger — mount, a 401 retry from client.ts, the scheduled timer —
  // must go through this single in-flight promise instead of calling
  // apiRefreshToken() directly.
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);
  // Lets scheduleRefresh's setTimeout call the latest doRefresh without the
  // two useCallbacks needing to reference each other out of declaration order.
  const doRefreshRef = useRef<() => Promise<string | null>>(async () => null);

  // client.ts's getToken() used to read `state.token` through a closure that
  // client.ts only received via a useEffect — and effects only run AFTER
  // React commits a render, one tick after setState. Any request fired right
  // after login()/doRefresh() resolves (a redirect to /map, a retried
  // request, react-query refetching immediately) could still read the OLD
  // closure and get a 401 despite the new token being logically "set". A
  // plain ref sidesteps that: applyAuth writes it synchronously, in the same
  // tick the new token becomes known, so getToken() is never behind.
  const tokenRef = useRef<string | null>(null);

  const applyAuth = useCallback((token: string | null, user: User | null) => {
    tokenRef.current = token;
    setState({
      user,
      token,
      isAuthenticated: token !== null,
      isLoading: false,
    });
  }, []);

  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const expiry = parseTokenExpiry(token);
    if (!expiry) return;

    const delay = expiry - Date.now() - REFRESH_BUFFER_MS;
    if (delay <= 0) return;

    refreshTimerRef.current = setTimeout(() => {
      void doRefreshRef.current();
    }, delay);
  }, []);

  const doRefresh = useCallback(async (): Promise<string | null> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const promise = (async () => {
      if (!tryAcquireRefreshLock()) {
        // A sibling tab is already mid-refresh — wait for it to broadcast
        // the new token instead of sending our own request with what's
        // about to become a stale (rotated-out) refresh cookie.
        const sibling = await waitForSiblingRefresh(BROADCAST_WAIT_MS);
        if (sibling?.accessToken) {
          applyAuth(sibling.accessToken, sibling.user ?? null);
          scheduleRefresh(sibling.accessToken);
          return sibling.accessToken;
        }
        // Sibling didn't deliver in time (crashed tab, closed mid-request) —
        // fall through and try the network ourselves.
      }

      try {
        const response = await apiRefreshToken();
        authChannel?.postMessage({
          type: 'refreshed',
          accessToken: response.accessToken,
          user: response.user,
        } satisfies BroadcastAuthMessage);
        applyAuth(response.accessToken, response.user);
        scheduleRefresh(response.accessToken);
        return response.accessToken;
      } catch {
        applyAuth(null, null);
        return null;
      } finally {
        releaseRefreshLock();
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = promise;
    return promise;
  }, [scheduleRefresh, applyAuth]);

  useEffect(() => {
    doRefreshRef.current = doRefresh;
  }, [doRefresh]);

  // Passive listener: adopt a sibling tab's refreshed token even when this
  // tab isn't the one contending for the lock right now (e.g. it's sitting
  // idle in the background) — keeps its own timer from firing needlessly
  // close to a refresh a sibling just did.
  useEffect(() => {
    if (!authChannel) return;
    function onMessage(event: MessageEvent<BroadcastAuthMessage>) {
      if (event.data.type !== 'refreshed' || !event.data.accessToken) return;
      applyAuth(event.data.accessToken, event.data.user ?? null);
      scheduleRefresh(event.data.accessToken);
    }
    authChannel.addEventListener('message', onMessage);
    return () => authChannel!.removeEventListener('message', onMessage);
  }, [scheduleRefresh, applyAuth]);

  // Configure the API client once — tokenRef.current is always current
  // (applyAuth writes it synchronously), so there's no need to re-register
  // on every token change the way reading `state.token` here would require.
  useEffect(() => {
    configureClient(() => tokenRef.current, doRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attempt silent refresh on mount (uses httpOnly refresh cookie) — routed
  // through doRefresh so it dedupes against any 401-triggered refresh that
  // fires around the same time.
  useEffect(() => {
    void doRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await apiLogin(email, password);
      applyAuth(response.accessToken, response.user);
      scheduleRefresh(response.accessToken);
    },
    [scheduleRefresh, applyAuth],
  );

  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    applyAuth(null, null);
  }, [applyAuth]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
